import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AsinWithListOfMarketplaces } from "./StamperWizard";
import { AsinMarketplaceSelectionLinks } from "./StamperTable";
import { AsinMarketplaceWeblab } from "./WeblabTable";
import { AppThunk } from "../app/store";
import { createCacheableNotificationDefinition } from "../notification/cacheableNotificationConnector";
import { getCurrentEpochMilliseconds } from "../../util/dateUtils";
import {
  AsinMarketplaceLaunchAttributes,
  AsinMarketplaceStampAttributes,
  AsinMarketplaceStampList,
  AsinMarketplaceWeblabAttributes,
  AsinMarketplaceWeblabList,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { getMarketplaceId } from "../../from-server/acs/acs-service";
import { publishStamps, publishWeblabs } from "../app/api";
import { createNotificationDefinition } from "../notification/notificationConnector";
import moment from "moment";

export interface LivenessState {
  liveness: string;
  waitingForLiveness: boolean;
}

export interface StamperWizardState {
  asinsWithListOfMarketplaces: AsinWithListOfMarketplaces[];
  asinsMarketplaceLiveness: { [key: string]: LivenessState };
  asinMarketplaceSelectionLinks: AsinMarketplaceSelectionLinks[];
  asinMarketplaceWeblab: AsinMarketplaceWeblab[];
  weblabReasonHashLookup: { [key: string]: string };
  touched: { [key: string]: boolean };
  weblabSelected: boolean;
  stampSelected: boolean;
  program?: string;
  programName?: string;
  partiness?: string;
  type?: string;
}

export const stamperWizardInitialState: StamperWizardState = {
  asinsWithListOfMarketplaces: [],
  asinsMarketplaceLiveness: {},
  asinMarketplaceSelectionLinks: [],
  asinMarketplaceWeblab: [],
  weblabReasonHashLookup: {},
  touched: {},
  weblabSelected: false,
  stampSelected: false,
  program: undefined,
  programName: undefined,
  partiness: undefined,
  type: undefined,
};

const stamperWizardState = createSlice({
  name: "stamperWizardState",
  initialState: stamperWizardInitialState,
  reducers: {
    setStamperWizardState(
      state: StamperWizardState,
      action: PayloadAction<{ item: string; value: LivenessState }>
    ) {
      const { item, value } = action.payload;
      state.asinsMarketplaceLiveness[item] = value;
    },
    setStamperWizardTouched(
      state: StamperWizardState,
      action: PayloadAction<{ item: string; value: boolean }>
    ) {
      const { item, value } = action.payload;
      state.touched[item] = value;
    },
    setStampSelected(state: StamperWizardState) {
      state.stampSelected = true;
    },
    unsetStampSelected(state: StamperWizardState) {
      state.stampSelected = false;
    },
    setWeblabSelected(state: StamperWizardState) {
      state.weblabSelected = true;
    },
    unsetWeblabSelected(state: StamperWizardState) {
      state.weblabSelected = false;
    },
    setProgramState(
      state: StamperWizardState,
      action: PayloadAction<{ program?: string; programName?: string }>
    ) {
      state.program = action.payload.program;
      state.programName = action.payload.programName;
    },
    setPartyState(
      state: StamperWizardState,
      action: PayloadAction<{ partiness?: string }>
    ) {
      state.partiness = action.payload.partiness;
    },
    setAsinTypeState(
      state: StamperWizardState,
      action: PayloadAction<{ type?: string }>
    ) {
      state.type = action.payload.type;
    },
    setAsinWithListOfMarketplaces(
      state: StamperWizardState,
      action: PayloadAction<{
        asinsWithListOfMarketplaces: AsinWithListOfMarketplaces[];
      }>
    ) {
      state.asinsWithListOfMarketplaces =
        action.payload.asinsWithListOfMarketplaces;
    },
    setAsinMarketplaceSelectionLinks(
      state: StamperWizardState,
      action: PayloadAction<{
        asinMarketplaceSelectionLinks: AsinMarketplaceSelectionLinks[];
      }>
    ) {
      state.asinMarketplaceSelectionLinks =
        action.payload.asinMarketplaceSelectionLinks;
    },
    setAsinMarketplaceWeblab(
      state: StamperWizardState,
      action: PayloadAction<{ asinMarketplaceWeblab: AsinMarketplaceWeblab[] }>
    ) {
      state.asinMarketplaceWeblab = action.payload.asinMarketplaceWeblab;
    },
    setAsinMarketplaceWeblabReason(
      state: StamperWizardState,
      action: PayloadAction<{ asinMp: string; weblabReason: string }>
    ) {
      state.weblabReasonHashLookup[action.payload.asinMp] =
        action.payload.weblabReason;
    },
  },
});

export const {
  setStamperWizardState,
  setStamperWizardTouched,
  setStampSelected,
  unsetStampSelected,
  setWeblabSelected,
  unsetWeblabSelected,
  setProgramState,
  setPartyState,
  setAsinTypeState,
  setAsinWithListOfMarketplaces,
  setAsinMarketplaceSelectionLinks,
  setAsinMarketplaceWeblab,
  setAsinMarketplaceWeblabReason,
} = stamperWizardState.actions;

export default stamperWizardState.reducer;

export function returnHome() {
  window.location.href = "/";
}
const publishStampErrorMessage = `
Next Steps:
- Verity all product information is correct
- Verify that selected marketplace(s) are valid
- Try refreshing page and submitting again
- Use give feedback in footer to report an issue
`;
export const publishStamp =
  (
    asinMarketplaces: AsinMarketplaceSelectionLinks[],
    program_participation: string
  ): AppThunk =>
  async (dispatch) => {
    const messageNotificationKey = moment.now();
    try {
      dispatch(
        createCacheableNotificationDefinition(
          "info",
          "PublishStamps preparing...",
          true,
          messageNotificationKey,
          `We are attempting to publish ${asinMarketplaces.length} ASIN/Marketplace stamps to Catalog.`
        )
      );
      const currentDate = getCurrentEpochMilliseconds();
      const asinMps = asinMarketplaces.map((item) => {
        return AsinMarketplaceLaunchAttributes.fromJson({
          asin: item.asin,
          obfuscatedMarketplaceId: getMarketplaceId(item.marketplace),
          indexSuppressed: null,
          thirdPartySiteExcluded: null,
          productSiteEmbargoDate: currentDate,
          productSiteLaunchDate: currentDate,
        });
      });
      const asinMarketplaceStampList = new AsinMarketplaceStampList(
        asinMps.map((asinMp) =>
          AsinMarketplaceStampAttributes.clone({
            asin: asinMp.asin,
            obfuscatedMarketplaceId: asinMp.obfuscatedMarketplaceId,
            programParticipation: program_participation,
          })
        )
      );

      await publishStamps(asinMarketplaceStampList)
        .then((result) => {
          dispatch(
            createCacheableNotificationDefinition(
              "success",
              "Publishing Stamps Submission Successful",
              true,
              messageNotificationKey,
              `We have successfully submitted ${asinMarketplaces.length} ASIN/Marketplace stamps to Catalog. In most cases, ` +
                `ASINs will appear in Devices ASIN Central within 30 minutes but could take up to 12 hours. ` +
                `Use Selection Central to troubleshoot issues blocking stamp (program_participation) reconciliation.`
            )
          );
        })
        .then(() => returnHome())
        .catch((reject) => {
          dispatch(
            createNotificationDefinition(
              "error",
              "PublishStamps Error",
              true,
              `${
                reject.response.data.message?.toString() ||
                "PublishStamp did not succeed in DevicesLaunchControlService"
              }` + `${publishStampErrorMessage}`
            )
          );
        });
    } catch (err: any) {
      const messageNotificationErrorKey = moment.now();
      dispatch(
        createCacheableNotificationDefinition(
          "error",
          err.toString(),
          true,
          messageNotificationErrorKey,
          undefined
        )
      );
    }
  };

export const publishWeblab =
  (asinMarketplaces: AsinMarketplaceWeblab[], programName: string): AppThunk =>
  async (dispatch) => {
    const messageNotificationKey = moment.now();
    try {
      dispatch(
        createCacheableNotificationDefinition(
          "info",
          "PublishWeblabs preparing...",
          true,
          messageNotificationKey,
          `We are attempting to Weblab ${asinMarketplaces.length} ASIN/Marketplace .`
        )
      );
      const asinMps = new AsinMarketplaceWeblabList(
        asinMarketplaces.map((item) => {
          return AsinMarketplaceWeblabAttributes.clone({
            asin: item.asin,
            obfuscatedMarketplaceId: getMarketplaceId(item.marketplaceId) || "",
            programName: programName,
            livenessStatus: item.liveness,
            weblabReason: item.reasonForWeblab,
          });
        })
      );

      await publishWeblabs(asinMps)
        .then((result) => {
          dispatch(
            createCacheableNotificationDefinition(
              "success",
              "Publishing Weblabs Submission Successful",
              true,
              messageNotificationKey,
              `We have successfully Weblab Gated ${asinMps.length} ASIN/Marketplace. You can check the ASIN/Marketplace detail pages to make sure you are re-rerouted.`
            )
          );
        })
        .then(() => returnHome())
        .catch((reject) => {
          dispatch(
            createNotificationDefinition(
              "error",
              "PublishWeblabs Error",
              true,
              `${
                reject.response.data.message?.toString() ||
                "PublishWeblab did not succeed in DevicesLaunchControlService"
              }` +
                `. \nUse submit a SIM Ticket link at the bottom of Devices ASIN Central if this continues`
            )
          );
        });
    } catch (err: any) {
      const messageNotificationErrorKey = moment.now();
      dispatch(
        createCacheableNotificationDefinition(
          "error",
          err.toString(),
          true,
          messageNotificationErrorKey,
          undefined
        )
      );
    }
  };

export const publishStampAndWeblab =
  (
    asinMarketplacesForStamp: AsinMarketplaceSelectionLinks[],
    asinMarketplacesForWeblab: AsinMarketplaceWeblab[],
    program_participation: string,
    programName: string
  ): AppThunk =>
  async (dispatch) => {
    const messageNotificationKeyStamp = moment.now();
    const messageNotificationKeyWeblab = moment.now() + 1;
    try {
      dispatch(
        createCacheableNotificationDefinition(
          "info",
          "PublishStamps preparing...",
          true,
          messageNotificationKeyStamp,
          `We are attempting to publish ${asinMarketplacesForStamp.length} ASIN/Marketplace stamps to Catalog.`
        )
      );
      const currentDate = getCurrentEpochMilliseconds();
      const asinMps = asinMarketplacesForStamp.map((item) => {
        return AsinMarketplaceLaunchAttributes.fromJson({
          asin: item.asin,
          obfuscatedMarketplaceId: getMarketplaceId(item.marketplace),
          indexSuppressed: null,
          thirdPartySiteExcluded: null,
          productSiteEmbargoDate: currentDate,
          productSiteLaunchDate: currentDate,
        });
      });
      const asinMarketplaceStampList = new AsinMarketplaceStampList(
        asinMps.map((asinMp) =>
          AsinMarketplaceStampAttributes.clone({
            asin: asinMp.asin,
            obfuscatedMarketplaceId: asinMp.obfuscatedMarketplaceId,
            programParticipation: program_participation,
          })
        )
      );
      dispatch(
        createCacheableNotificationDefinition(
          "info",
          "PublishWeblabs preparing...",
          true,
          messageNotificationKeyWeblab,
          `We are attempting to publish ${asinMarketplacesForWeblab.length} ASIN/Marketplace stamps to Catalog.`
        )
      );
      const asinMarketplaceWeblabList = new AsinMarketplaceWeblabList(
        asinMarketplacesForWeblab.map((item) => {
          return AsinMarketplaceWeblabAttributes.clone({
            asin: item.asin,
            obfuscatedMarketplaceId: getMarketplaceId(item.marketplaceId) || "",
            programName: programName,
            livenessStatus: item.liveness,
            weblabReason: item.reasonForWeblab,
          });
        })
      );

      await publishStamps(asinMarketplaceStampList)
        .then((result) => {
          dispatch(
            createCacheableNotificationDefinition(
              "success",
              "Publishing Stamps Submission Successful",
              true,
              messageNotificationKeyStamp,
              `We have successfully submitted ${asinMarketplaceStampList.length} ASIN/Marketplace stamps to Catalog. In most cases, ` +
                `ASINs will appear in Devices ASIN Central within 30 minutes but could take up to 12 hours. ` +
                `Use Selection Central to troubleshoot issues blocking stamp (program_participation) reconciliation.`
            )
          );
        })
        .catch((reject) => {
          dispatch(
            createNotificationDefinition(
              "error",
              "PublishStamps Error",
              true,
              `${
                reject.response.data.message?.toString() ||
                "PublishStamp did not succeed in DevicesLaunchControlService"
              }` + `${publishStampErrorMessage}`
            )
          );
        })
        .then(() =>
          publishWeblabs(asinMarketplaceWeblabList)
            .then((result) => {
              dispatch(
                createCacheableNotificationDefinition(
                  "success",
                  "Publishing Weblabs Submission Successful",
                  true,
                  messageNotificationKeyWeblab,
                  `We have successfully Weblab Gated ${asinMarketplaceWeblabList.length} ASIN/Marketplace. You can check the ASIN/Marketplace detail pages to make sure you are re-rerouted.`
                )
              );
            })
            .catch((reject) => {
              dispatch(
                createNotificationDefinition(
                  "error",
                  "PublishWeblabs Error",
                  true,
                  `${
                    reject.response.data.message?.toString() ||
                    "PublishWeblab did not succeed in DevicesLaunchControlService"
                  }` +
                    `. \nUse submit a SIM Ticket link at the bottom of Devices ASIN Central if this continues`
                )
              );
            })
        )
        .then(() => returnHome());
    } catch (err: any) {
      const messageNotificationErrorKey = moment.now();
      dispatch(
        createCacheableNotificationDefinition(
          "error",
          err.toString(),
          true,
          messageNotificationErrorKey,
          undefined
        )
      );
    }
  };
